import React, { useContext } from 'react';
import i18next from 'core/i18n';
import { AgFinancingCreditAnalysisStatus, Credit } from '@indigo-ag/schema';
import { Body, palettes, SmallBody, SmallTitle, TinyTitle } from '@indigo-ag/component-module';

import { UserContext } from 'core/components/GateWay/GateWay';
import { Permissions, CreditUtil, UserUtil, EnhancedExpansionPanel } from 'shared';
import { PendenciesList } from 'features/brazil-grower/components/BrazilGrowerFinance/shared/PendenciesSection/PendenciesList/PendenciesList';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import { NotificationSection, NotificationSectionStatus } from '../../NotificationSection/NotificationSection';
import { FinancialInformationSectionSummary } from '../FinancialInformationSection/FinancialInformationSection';
import { creditUtils } from '../shared';
import {
  ClientDataSectionSummary,
  ExploredAreasSectionSummary,
  IncomeStatementSectionSummary,
  JudicialActionsSectionSummary,
  ParticipationSectionSummary,
  PesquisaSerasaSectionSummary,
  ScoreSectionSummary,
  NotesSectionSummary
} from '..';
import { GuarantorBackgroundCheck } from '../../../features/credit-analysis/sections/GuarantorsSection/components';

const creditNotificationStateTitles: { [key in AgFinancingCreditAnalysisStatus]?: string } = {
  [AgFinancingCreditAnalysisStatus.COMPLETED]: i18next.t('completed'),
  [AgFinancingCreditAnalysisStatus.STARTED]: i18next.t('in_process'),
  [AgFinancingCreditAnalysisStatus.ON_HOLD]: i18next.t('onHold'),
  // * deprecated
  [AgFinancingCreditAnalysisStatus.APPROVED]: i18next.t('completed'),
  [AgFinancingCreditAnalysisStatus.DENIED]: i18next.t('denied'),
  [AgFinancingCreditAnalysisStatus.WAITING_CREDIT_MANAGER_DECISION]: i18next.t('sentToCommittee')
};

interface CreditAnalysisStateProps {
  creditInfo: Credit;
}

export const CreditAnalysisState: React.FunctionComponent<CreditAnalysisStateProps> = ({ creditInfo }) => {
  const {
    notes,
    markAsCompleteAt,
    markAsCompleteBy,
    decisionChangedAt,
    decisionChangedBy,
    status,
    decisionNotes,
    startedBy,
    startedAt
  } = creditInfo;

  /**
   * User Context
   */
  const { hasPermission } = useContext(UserContext);
  const isFlc = hasPermission(Permissions.FA_EXTERNAL_CREDIT);

  /**
   * Finance Process Context
   */
  const { agFinance, contactRolesBackgroundChecksQuery } = useContext(FinanceProcessContext);
  const serasaDataApi = contactRolesBackgroundChecksQuery?.data?.serasaReport;

  const getNotificationUpdatedAt = () => {
    if (CreditUtil.hasBeenSent(status)) return markAsCompleteAt;
    if (CreditUtil.hasNotBeenSent(status)) return startedAt;
    return decisionChangedAt;
  };

  const getNotificationUpdatedBy = () => {
    if (CreditUtil.hasBeenSent(status)) return markAsCompleteBy;
    if (CreditUtil.hasNotBeenSent(status)) return startedBy;
    return UserUtil.getUserFullName(decisionChangedBy);
  };

  const getNotificationStatus = (): NotificationSectionStatus => {
    if (CreditUtil.hasNotBeenSent(status) || CreditUtil.isOnHold(status)) {
      return 'warning';
    }
    if (CreditUtil.hasBeenSent(status) || CreditUtil.hasBeenApproved(status)) {
      return 'success';
    }
    return 'error';
  };

  const getCreditDossierDetail = (title: string, content: React.ReactNode) => {
    return (
      <EnhancedExpansionPanel title={<SmallTitle className="text-transform-capitalize">{title}</SmallTitle>}>
        {content}
      </EnhancedExpansionPanel>
    );
  };

  const renderCreditSummary = (credit: Credit) => (
    <>
      {getCreditDossierDetail(
        i18next.t('participation'),
        <ParticipationSectionSummary participationInfo={creditUtils.getParticipationInfoFromCredit(credit)} />
      )}
      {getCreditDossierDetail(
        i18next.t('clientData'),
        <ClientDataSectionSummary clientDataInfo={creditUtils.getClientDataInfoFromCredit(credit)} />
      )}
      {getCreditDossierDetail(
        i18next.t('serasaSearch_title'),
        <PesquisaSerasaSectionSummary pesquisaSerasaInfo={creditUtils.mapSerasaReportToCreditInput(serasaDataApi)} />
      )}
      {getCreditDossierDetail(
        i18next.t('judicialActions'),
        <JudicialActionsSectionSummary judicialActionsInfo={creditUtils.getJudicialActionsInfoFromCredit(credit)} />
      )}
      {getCreditDossierDetail(
        i18next.t('incomeTax'),
        <IncomeStatementSectionSummary incomeStatementInfo={creditUtils.getIncomeStatementInfoFromCredit(credit)} />
      )}
      {getCreditDossierDetail(
        i18next.t('exploredAreas'),
        <ExploredAreasSectionSummary exploredAreasInfo={creditUtils.getExploredAreasInfoFromCredit(credit)} />
      )}
      {getCreditDossierDetail(
        i18next.t('financialInformation'),
        <FinancialInformationSectionSummary financialInformationInfo={creditUtils.getFinancialInfoFromCredit(credit)} />
      )}
      {getCreditDossierDetail(
        i18next.t('scoreConsulting'),
        <ScoreSectionSummary scoreSectionInfo={creditUtils.getConsultingScoreInfoFromCredit(credit)} />
      )}
      {getCreditDossierDetail(
        i18next.t('notes'),
        <NotesSectionSummary notesSectionInfo={creditUtils.getNotesInfoFromCredit(credit)} />
      )}
    </>
  );

  return (
    <NotificationSection
      status={getNotificationStatus()}
      title={creditNotificationStateTitles[status] ?? ''}
      updatedAt={getNotificationUpdatedAt()}
      updateBy={getNotificationUpdatedBy()}
    >
      {decisionNotes && (
        <>
          <TinyTitle className="mt-2 mb-1 text-transform-capitalize" style={{ color: palettes.brand.gray[700] }}>
            {i18next.t('decisionNotes')}
          </TinyTitle>
          <Body className="mt-1 mb-1 break-line">{decisionNotes}</Body>
        </>
      )}
      <div className="flex">
        <div className="mr-5">
          <SmallBody style={{ color: palettes.brand.gray[700] }}>{i18next.t('scoring')}</SmallBody>
          <SmallBody className="mt-1">{creditInfo.scoreType ?? '-'}</SmallBody>
        </div>
        <div>
          <SmallBody style={{ color: palettes.brand.gray[700] }}>{i18next.t('number')}</SmallBody>
          <SmallBody className="mt-1">{creditInfo.scoreNumber ?? '-'}</SmallBody>
        </div>
      </div>
      {notes && (
        <>
          <TinyTitle className="mt-4 mb-1" style={{ color: palettes.brand.gray[700] }}>
            {i18next.t('commentsOrRecommendations')}
          </TinyTitle>
          <Body className="mt-1 break-line">{notes}</Body>
        </>
      )}
      {!isFlc && (
        <div className="mt-4">
          <EnhancedExpansionPanel
            title={<SmallTitle>{`${i18next.t('analysis')} - ${i18next.t('creditDossier')}`}</SmallTitle>}
          >
            <div className="width-100">{renderCreditSummary(creditInfo)}</div>
          </EnhancedExpansionPanel>
          {agFinance?.guarantorProcesses?.length ? (
            <EnhancedExpansionPanel
              title={<SmallTitle className="text-transform-capitalize">{i18next.t('guarantorProcesses')}</SmallTitle>}
            >
              <div className="width-100">
                {agFinance.guarantorProcesses.map(g => (
                  <div className="mb-4" key={`guarantor-${g.id}`}>
                    <SmallTitle className="text-transform-capitalize mb-3">
                      {g.guaranteedBy.name.toLowerCase()}
                    </SmallTitle>
                    <div className="width-100">
                      <GuarantorBackgroundCheck guarantorProcess={g} />
                      <EnhancedExpansionPanel
                        title={
                          <SmallTitle className="text-transform-capitalize">{i18next.t('creditAnalysis')}</SmallTitle>
                        }
                      >
                        <div className="width-100">{renderCreditSummary(g.creditAnalysis!)}</div>
                      </EnhancedExpansionPanel>
                    </div>
                  </div>
                ))}
              </div>
            </EnhancedExpansionPanel>
          ) : null}
          {creditInfo.pendencies?.length ? (
            <EnhancedExpansionPanel title={<SmallTitle>{i18next.t('pendencies')}</SmallTitle>}>
              <PendenciesList pendencies={creditInfo.pendencies} readonly />
            </EnhancedExpansionPanel>
          ) : null}
        </div>
      )}
    </NotificationSection>
  );
};
